import * as React from 'react';

/**
 * Largest categorical slot defined in tokens/data-viz.css (--chart-1..12).
 * Slots 1-8 follow the Okabe-Ito colorblind-safe ordering principle; ask for
 * colors in order and the series stay distinguishable under deuteranopia.
 */
export const CHART_SERIES_COUNT = 12;

/**
 * Canonical categorical chart palette — the light-theme values from
 * tokens/data-viz.css (`--chart-1..12`) as static `hsl(...)` strings.
 *
 * This is the single source of truth the chart components default their series
 * colors to (so a caller that passes no explicit color lands on the
 * colorblind-safe ramp automatically), and it doubles as the SSR / pre-hydration
 * fallback for {@link useChartColors} — a server-rendered chart is on-palette
 * until hydration re-reads the live CSS variables. Slots 1-8 follow the
 * Okabe-Ito colorblind-safe ordering; consume them in order.
 *
 * Prefer {@link useChartColors} in client components so dark mode / density flips
 * re-resolve the live tokens; reach for this constant only where a hook can't run
 * (SSR-only paths, module-scope defaults).
 */
export const CHART_SERIES: readonly string[] = [
  'hsl(252 84% 58%)',
  'hsl(186 88% 38%)',
  'hsl(18 92% 52%)',
  'hsl(152 70% 36%)',
  'hsl(38 95% 50%)',
  'hsl(210 92% 48%)',
  'hsl(290 70% 55%)',
  'hsl(335 75% 55%)',
  'hsl(170 65% 42%)',
  'hsl(52 85% 50%)',
  'hsl(220 30% 50%)',
  'hsl(310 55% 58%)',
];

/**
 * Pick the categorical-ramp color for a zero-based series index, wrapping past
 * slot 12. The shared default for chart components: `color ?? chartSeriesColor(i)`.
 */
export function chartSeriesColor(index: number): string {
  return CHART_SERIES[index % CHART_SERIES.length] ?? CHART_SERIES[0]!;
}

/** @deprecated SSR-fallback alias retained for internal use; see {@link CHART_SERIES}. */
const FALLBACK_SERIES = CHART_SERIES;

function resolveChartColors(count: number): string[] {
  const clamped = Math.max(1, Math.min(count, CHART_SERIES_COUNT));
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return FALLBACK_SERIES.slice(0, clamped) as string[];
  }
  const styles = getComputedStyle(document.documentElement);
  return Array.from({ length: clamped }, (_, i) => {
    const raw = styles.getPropertyValue(`--chart-${i + 1}`).trim();
    return raw ? `hsl(${raw})` : (FALLBACK_SERIES[i] ?? FALLBACK_SERIES[0]!);
  });
}

/**
 * Resolved categorical series colors from the data-viz ramp
 * (tokens/data-viz.css, `--chart-1..12`).
 *
 * SVG presentation attributes can't evaluate `var()`, so Recharts props like
 * `stroke`/`fill` need *resolved* color strings — this hook reads the
 * computed CSS variables off `<html>` and re-reads them when the theme class
 * flips, so dark mode picks up the lifted-lightness dark ramp. Modules should
 * consume this instead of hand-picking palette hexes per chart.
 *
 *   const colors = useChartColors(3);
 *   <Line stroke={colors[0]} /> <Line stroke={colors[1]} />
 *
 * For shadcn `ChartContainer` configs (which inject CSS rules where `var()`
 * does resolve), `hsl(var(--chart-n))` strings remain fine — this hook is for
 * the raw-Recharts / raw-SVG path.
 */
export function useChartColors(count: number = 5): string[] {
  const [colors, setColors] = React.useState<string[]>(() =>
    resolveChartColors(count)
  );

  React.useEffect(() => {
    setColors(resolveChartColors(count));
    // Theme flips toggle the `dark` class (and density modes swap
    // data-density) on <html>; re-resolve the ramp when either changes.
    const observer = new MutationObserver(() => {
      setColors(resolveChartColors(count));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-density', 'style'],
    });
    return () => observer.disconnect();
  }, [count]);

  return colors;
}

export interface ChartSeriesLegendItem {
  /** Series label shown next to the swatch. */
  label: React.ReactNode;
  /**
   * Explicit swatch color. Omit to take the next slot of the data-viz ramp
   * in order — the recommended path, since the ramp ordering is
   * colorblind-safe.
   */
  color?: string;
}

export interface ChartSeriesLegendProps
  extends React.HTMLAttributes<HTMLDivElement> {
  items: ChartSeriesLegendItem[];
}

/**
 * Horizontal swatch legend for the pure-SVG chart wrappers (Sparkline,
 * TimeSeries…) and raw-Recharts charts. Items without an explicit `color`
 * are assigned the data-viz ramp in order via {@link useChartColors}, so a
 * chart and its legend always agree on series colors. (The shadcn
 * `ChartLegend` in primitives/chart.tsx stays the choice when you're already
 * inside a `ChartContainer`.)
 */
export function ChartSeriesLegend({
  items,
  className,
  ...props
}: ChartSeriesLegendProps) {
  const ramp = useChartColors(Math.max(items.length, 1));
  return (
    <div
      role="list"
      className={[
        'flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {items.map((item, i) => (
        <span role="listitem" key={i} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2.5 shrink-0 rounded-[2px]"
            style={{ backgroundColor: item.color ?? ramp[i % ramp.length] }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}
